import type { AccountStatus, CloudCache, DeviceRow } from "./account";
import type { Session } from "./auth";

/**
 * Which full-screen gate (or the app) to show, decided exactly like the current app's
 * `cloudGate` and `handleStatus`. Pure: the caller does the storage writes it returns.
 */
export type GateView =
  | { view: "app" }
  | { view: "checking"; message?: string }
  | { view: "sign-in"; notice?: string }
  | { view: "need-licence"; email?: string; error?: string; trialEnded?: { plan: string; endedAt: string } }
  | { view: "limit"; email?: string; max: number; plan?: string; devices: DeviceRow[]; moveAt?: string | null }
  | { view: "blocked"; email?: string; reason?: string }
  | { view: "offline" };

export interface GateStep {
  next: GateView;
  /** New value for the cloud cache: a value to save, null to clear, undefined to leave as is. */
  cloud?: CloudCache | null;
  /** Sign out locally first (another device signed this one out). */
  signOut?: boolean;
}

/** Re-check the account in the background when it's been longer than this (`cloudGate`). */
export const RECHECK_MS = 3600e3;

/** On open: straight in if this device was approved for this user before (works offline); otherwise check. */
export function openDecision(session: Session | null, cloud: CloudCache | null, returnedFromSignIn: boolean, now: number) {
  if (!session) return { next: { view: "sign-in" } as GateView, check: false, recheck: false };
  if (!returnedFromSignIn && cloud?.state === "ok" && cloud.uid === session.uid) {
    return { next: { view: "app" } as GateView, check: false, recheck: !cloud.ent || now - (cloud.checked || 0) > RECHECK_MS };
  }
  return { next: { view: "checking" } as GateView, check: true, recheck: false };
}

/** After the account function answers. */
export function afterStatus(r: AccountStatus, session: Session | null, cached: CloudCache | null, now: number): GateStep {
  switch (r.state) {
    case "ok":
      return {
        next: { view: "app" },
        cloud: {
          uid: session?.uid, email: r.email, name: r.name || session?.name || "", licence: r.licence,
          ent: r.ent ?? null, devices: r.devices ?? [], state: "ok", checked: now,
        },
      };
    case "signed-out":
      return { next: { view: "sign-in", notice: "You were signed out. Sign in again to carry on." }, cloud: null };
    case "revoked":
      return { next: { view: "sign-in", notice: "This device was signed out from another device. Sign in again to use Hachi Garage here." }, cloud: null, signOut: true };
    case "need-licence":
      return { next: { view: "need-licence", ...(r.email ? { email: r.email } : {}), ...(r.error ? { error: r.error } : {}), ...(r.trialEnded ? { trialEnded: r.trialEnded } : {}) }, cloud: null };
    case "limit":
      return { next: { view: "limit", ...(r.email ? { email: r.email } : {}), max: r.max, ...(r.plan ? { plan: r.plan } : {}), devices: r.devices ?? [], ...(r.moveAt !== undefined ? { moveAt: r.moveAt } : {}) }, cloud: null };
    case "blocked":
      return { next: { view: "blocked", ...(r.email ? { email: r.email } : {}), ...(r.reason ? { reason: r.reason } : {}) }, cloud: null };
    default:
      // Server or network trouble: keep working if this device was already approved for this user.
      if (cached?.state === "ok" && session && cached.uid === session.uid) return { next: { view: "app" } };
      return { next: { view: "offline" } };
  }
}

/** Gate messages for licence errors (`CMSG`). */
export const LICENCE_MESSAGES: Readonly<Record<string, string>> = {
  invalid: "That license key wasn't found. Copy it again from your purchase email and check for missing characters.",
  taken: "This license key is already linked to a different account. Sign in with that account, or message @hachi540i to move it.",
  refunded: "This purchase was refunded or canceled, so its license no longer works.",
  "addon-first": "This is an add-on key (extra cars or a device). Enter your main Hachi Garage license key first, then add this one in Plan & add-ons.",
  server: "The license server didn't respond. Try again in a minute.",
  network: "Couldn't connect. Check your internet connection and try again.",
  "trial-used": "You've already used your free trial. Pick a plan below to keep going. Your records are safe.",
  "trial-off": "Free trials aren't available right now. Pick a plan below.",
};

/** The confirmation after a server answer that added something, once (`handleStatus`, `afterKey`). */
export function addedMessage(r: AccountStatus): string | null {
  if (r.state !== "ok") return null;
  if (r.trialStarted) return `Your ${r.trialStarted.plan === "shop" ? "Shop" : "Enthusiast"} trial has started. Enjoy!`;
  if (r.moved) return `Moved here from ${r.moved.label}. That device is signed out.`;
  if (r.autoAdded?.length) return `${r.autoAdded.length === 1 ? "Purchase added: " : "Purchases added: "}${r.autoAdded.join(", ")}`;
  if (r.added) return r.added.already ? "That key is already on your account" : `Added: ${r.added.label}`;
  return null;
}
